import { Alert, Typography } from "antd";
import CustomButton from "../CustomButton/Button";
import { useSelector } from "react-redux";
import { useNavigate, useLocation } from "react-router-dom";
import { BTN_TXT, GLOBAL_CONSTANTS } from "../../constants/constants";
import {
  DEAL_CATEGORY_IDS,
  isInvoiceStyleCategory,
} from "../../helpers/dealCategoryProducts";
import "./Banner.scss";

const { Text } = Typography;

const Banner = () => {
  const navigate = useNavigate();
  const location = useLocation();
  const { user } = useSelector((state) => state.auth);
  const { activeCategory } = useSelector((state) => state.dealCategory);
  const { pendingSla, pendingNda } = useSelector((state) => state.esign);
  const { onboardingStepStatus } = useSelector((state) => state.questionnaire);
  const categoryStatus =
    activeCategory?.statusId || user?.dealCategories?.[0]?.statusId;
  const dealCategoryId =
    activeCategory?.dealCategoryId || user?.dealCategories?.[0]?.dealCategoryId;
  const isKycBannerEligible = isInvoiceStyleCategory(dealCategoryId);
  const isJointVentures = dealCategoryId === DEAL_CATEGORY_IDS.JOINT_VENTURES;
  const isOnCompleteKycPage = location.pathname === "/complete-kyc";

  // Prefer live onboarding status from API (admin may complete KYC while business session is stale)
  const kycVerifiedFromApi = onboardingStepStatus?.KYC_Verified === true;
  const kycReviewFromApi =
    onboardingStepStatus?.KYC_Review === true && !kycVerifiedFromApi;
  const kycPendingFromApi =
    onboardingStepStatus?.KYC_Pending === true &&
    !kycReviewFromApi &&
    !kycVerifiedFromApi;

  // KYC completion banner is for Invoice Financing & Supply Chain Financing; JV uses questionnaire-based KYC flow
  const needsKycCompletion =
    isKycBannerEligible &&
    !isOnCompleteKycPage &&
    (onboardingStepStatus
      ? kycPendingFromApi
      : categoryStatus === GLOBAL_CONSTANTS.STAT_KYC_PEN_ID);
  const kycUnderReview =
    isKycBannerEligible &&
    (onboardingStepStatus
      ? kycReviewFromApi
      : categoryStatus === GLOBAL_CONSTANTS.STAT_KYC_REVIEW_ID);

  // SLA/NDA are JV-only — hide when viewing Invoice Financing
  const needsSla =
    isJointVentures &&
    pendingSla?.isSent &&
    !pendingSla?.isSigned &&
    pendingSla?.invitation_link;
  const needsNda =
    isJointVentures &&
    pendingNda?.isSent &&
    !pendingNda?.isSigned &&
    pendingNda?.invitation_link;

  const handleCompleteKyc = () => {
    if (isOnCompleteKycPage) return;
    navigate("/complete-kyc");
  };

  const kycPendingBanner = (
    <div className="banner-content">
      <Text strong style={{ fontSize: 14, color: "#332E1C" }}>
        {GLOBAL_CONSTANTS.COMPLETE_KYC_TXT}
      </Text>
      <CustomButton
        type="primary"
        style={{ backgroundColor: "#533F02", borderRadius: 8 }}
        onClick={handleCompleteKyc}
      >
        {BTN_TXT.COMPLETE_NOW}
      </CustomButton>
    </div>
  );

  const kycUnderReviewBanner = (
    <div className="banner-content">
      <Text strong style={{ fontSize: 14, color: "#332E1C" }}>
        {GLOBAL_CONSTANTS.KYC_UNDER_REVIEW_TXT}
      </Text>
    </div>
  );

  const renderSlaNdaBanner = () => {
    if (!needsSla && !needsNda) {
      return null;
    }

    let bannerMessage = GLOBAL_CONSTANTS.SIGN_SLA_AND_NDA_TXT;
    if (needsSla && !needsNda) {
      bannerMessage = GLOBAL_CONSTANTS.SIGN_SLA_ONLY_TXT;
    } else if (!needsSla && needsNda) {
      bannerMessage = GLOBAL_CONSTANTS.SIGN_NDA_ONLY_TXT;
    }

    return (
      <div className="banner-content">
        <Text strong style={{ fontSize: 14, color: "#332E1C" }}>
          {bannerMessage}
        </Text>
        {needsSla && (
          <CustomButton
            type="primary"
            style={{ backgroundColor: "#533F02", borderRadius: 8 }}
            onClick={() => navigate("/sla")}
          >
            {BTN_TXT.SIGN_SLA}
          </CustomButton>
        )}
        {needsNda && (
          <CustomButton
            type="primary"
            style={{ backgroundColor: "#533F02", borderRadius: 8 }}
            onClick={() => navigate("/nda")}
          >
            {BTN_TXT.SIGN_NDA}
          </CustomButton>
        )}
      </div>
    );
  };

  const renderBanner = () => {
    if (needsKycCompletion) {
      return kycPendingBanner;
    }

    if (kycUnderReview) {
      return kycUnderReviewBanner;
    }

    return renderSlaNdaBanner();
  };

  const bannerContent = renderBanner();

  if (!bannerContent) {
    return null;
  }

  return (
    <Alert
      banner
      showIcon={false}
      className="custom-banner"
      message={bannerContent}
    />
  );
};

export default Banner;
